import { getS3PreSignedUrl } from '@apis/api-helper';
import { Buffer } from 'buffer';
import { getErrorString } from './utils';
import { Toast } from '@common-web';
import Resizer from 'react-image-file-resizer';

export function resize(image) {
  return new Promise((resolve) => {
    Resizer?.imageFileResizer(
      image,
      1000,
      1000,
      'JPEG',
      100,
      0,
      (uri) => {
        resolve(uri);
      },
      'base64'
    );
  });
}

export function uploadFileOnS3(file, s3Data, isImageFile) {
  return new Promise(function (resolve, reject) {
    try {
      if (s3Data) {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', s3Data.url);
        xhr.onreadystatechange = () => {
          if (xhr.readyState === 4) {
            if (xhr.status === 200) {
              resolve(xhr);
              console.log('Image successfully uploaded to S3');
              return xhr.status;
            } else {
              console.log('Error while sending the image to S3. Status:', xhr.status);
            }
          }
        };
        xhr.onerror = (e) => {
          reject(e);
          console.log('Image uploading to s3 failure', e);
        };

        const type = isImageFile ? file.split(';')[0].split(':')[1] : file.type;

        xhr.setRequestHeader('Content-Type', type);

        if (isImageFile) {
          xhr.setRequestHeader('Content-Encoding', 'base64');
        }

        let buffer = file;

        if (isImageFile) {
          buffer = Buffer.from(file.replace(/^data:image\/\w+;base64,/, ''), 'base64');
        }
        xhr.send(buffer);
      } else {
        console.log('Cannot upload image on S3, data is not correct.');
      }
    } catch (error) {
      console.log('catch (error)', error);
    }
  });
}

export function getUUIDForLocalFile(attachmentType, associationKey, fileName, contentType) {
  let count = 0;
  let latestError;
  return new Promise((resolve, reject) => {
    const tryGetS3PreSignedUrl = async () => {
      try {
        const res = await getS3PreSignedUrl(attachmentType, associationKey, fileName);
        if (res && res.data) {
          resolve(res.data);
        } else {
          reject(new Error('Failed to get pre-signed URL'));
        }
      } catch (e) {
        latestError = e;
        count += 1;
        if (count < 3) {
          tryGetS3PreSignedUrl();
        } else {
          reject(latestError);
        }
      }
    };

    tryGetS3PreSignedUrl();
  });
}

export function getBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });
}

export const updateUuid = ({ file, updated, isError = false, files }) => {
  const filesData = [...files];
  const index = filesData.findIndex((e) => file?.uid === e?.uid);
  if (isError) {
    filesData[index].id = updated.id;
    filesData[index].hasUuid = updated.hasUuid;
    filesData[index].uploaded = updated.uploaded;
    filesData[index].uploading = updated.uploading;
    filesData[index].inError = updated.inError;
  } else {
    filesData[index].id = updated.id;
    filesData[index].hasUuid = updated.hasUuid;
  }
  return [...filesData];
};

export const updatePreSignedUrl = ({ updated, isError = false, files }) => {
  const filesData = [...files];
  const index = filesData.findIndex((e) => e.id === updated.id);
  if (isError) {
    filesData[index].uploaded = updated.uploaded;
    filesData[index].uploading = updated.uploading;
    filesData[index].inError = updated.inError;
    filesData[index].hasPreSignedUrl = updated.hasPreSignedUrl;
  } else {
    filesData[index].hasUuid = updated.hasUuid;
    filesData[index].hasPreSignedUrl = updated.hasPreSignedUrl;
    filesData[index].preview = updated.preview;
  }
  return filesData;
};

export async function handleFileUploadChange({
  file,
  fileList,
  attachmentType,
  associationKey,
  setter = () => {},
  loadingSetter = () => {},
}) {
  loadingSetter(true);
  let isImageFile = file.type.includes('image');
  let fileData = file;
  if (isImageFile) {
    fileData = await resize(file);
  }
  let presignedUrlResponse;
  let updateFiles = [];
  let updatePreSignedUrlFiles = [];
  try {
    presignedUrlResponse = await getUUIDForLocalFile(attachmentType, associationKey, file.name, null);
    if (presignedUrlResponse?.id) {
      const data = { id: presignedUrlResponse.id, hasUuid: true };
      updateFiles = updateUuid({
        file,
        updated: data,
        files: fileList,
      });
    }
  } catch (error) {
    const data = {
      id: file?.uid,
      hasUuid: false,
      uploaded: false,
      uploading: false,
      inError: true,
    };
    updateFiles = updateUuid({
      file,
      updated: data,
      isError: true,
      files: fileList,
    });
    Toast.error(getErrorString(error));
  }

  if (presignedUrlResponse && presignedUrlResponse.id) {
    let res;
    try {
      res = await uploadFileOnS3(fileData, presignedUrlResponse, isImageFile);
    } catch (error) {
      Toast.error(getErrorString(error));
    }
    if (res) {
      let preview;
      try {
        preview = await getBase64(file);
      } catch (error) {
        Toast.error(getErrorString(error));
      }
      let data = {
        hasUuid: true,
        hasPreSignedUrl: true,
        id: presignedUrlResponse.id,
        preview: preview,
      };
      updatePreSignedUrlFiles = updatePreSignedUrl({
        updated: data,
        files: updateFiles,
      });
    }
  }
  loadingSetter(false);
  setter(updatePreSignedUrlFiles);
  return updatePreSignedUrlFiles;
}

export function handleImageRemove({ file, files, setter = () => {} }) {
  const filesList = [...files];
  const index = filesList?.findIndex((e) => e?.uid === file?.uid);
  if (index > -1) {
    filesList?.splice(index, 1);
    setter(filesList);
  }
}

export const openPdfInNewTab = (base64String) => {
  const cleanBase64 = base64String.split(',')[1];

  const byteCharacters = atob(cleanBase64);
  const byteArray = new Uint8Array(byteCharacters.length);

  for (let i = 0; i < byteCharacters.length; i++) {
    byteArray[i] = byteCharacters.charCodeAt(i);
  }

  const pdfBlob = new Blob([byteArray], { type: 'application/pdf' });

  const pdfUrl = URL.createObjectURL(pdfBlob);

  window.open(pdfUrl, '_blank');
};

export const isPDFBlob = (str = '') => {
  const regex = /^data:application\/pdf;base64,([A-Za-z0-9+/=]+)$/;
  return regex.test(str);
};

export function createFileFromString(imageString, fileName = 'image.png') {
  return new Promise((resolve, reject) => {
    if (imageString.startsWith('data:')) {
      const base64Data = imageString.split(',')[1];
      const byteCharacters = atob(base64Data);
      const byteArrays = [];
      for (let offset = 0; offset < byteCharacters.length; offset++) {
        byteArrays.push(byteCharacters.charCodeAt(offset));
      }
      const byteArray = new Uint8Array(byteArrays);
      const blob = new Blob([byteArray], { type: 'image/png' });
      const file = new File([blob], fileName, { type: 'image/png' });
      resolve({ file, dataUrl: imageString });
    } else {
      fetch(imageString)
        .then((response) => response.blob())
        .then((blob) => {
          const file = new File([blob], fileName, { type: blob.type });
          resolve({ file, dataUrl: imageString });
        })
        .catch(reject);
    }
  });
}
